import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * Plan tier hierarchy (highest to lowest)
 */
const PLAN_TIERS = {
  'career_boost_elite': { rank: 5, label: 'Career Boost Elite', color: 'text-[#CE1126]', badge: 'bg-red-100 text-red-800' },
  'job_apply_kit': { rank: 4, label: 'Job Apply Kit', color: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-800' },
  'interview_pack': { rank: 3, label: 'Interview Pack', color: 'text-amber-600', badge: 'bg-amber-100 text-amber-800' },
  'pro': { rank: 2, label: 'Pro Plan', color: 'text-[#009639]', badge: 'bg-green-100 text-green-800' },
  'free': { rank: 0, label: 'Free', color: 'text-gray-600', badge: 'bg-gray-100 text-gray-700' },
};

/**
 * Human-readable benefit labels for usage_remaining keys
 */
const BENEFIT_LABELS = {
  cv_fix_downloads: 'ATS CV Fixes',
  cover_letter_generations: 'Cover Letters',
  email_templates: 'Email Templates',
  whatsapp_templates: 'WhatsApp Templates',
  english_test: 'English Assessment',
  cv_checks: 'CV Checks',
};

/**
 * Full benefit descriptions per plan (for display)
 */
const PLAN_BENEFITS = {
  'career_boost_elite': [
    { key: 'cv_fix_downloads', label: '15 ATS CV Fixes' },
    { key: 'linkedin', label: 'LinkedIn Profile Optimization' },
    { key: 'cover_letter_generations', label: 'Cover Letter Access' },
    { key: 'email_templates', label: 'Email Apply Templates' },
    { key: 'whatsapp_templates', label: 'WhatsApp Apply Templates' },
    { key: 'interview', label: 'Interview Prep Access' },
    { key: 'priority', label: 'Priority Support' },
    { key: 'candidate_lock', label: 'Same Candidate Protection' },
  ],
  'job_apply_kit': [
    { key: 'cv_checks', label: '15 CV Checks' },
    { key: 'cover_letter_generations', label: 'Cover Letter Access' },
    { key: 'email_templates', label: 'Email Apply Templates' },
    { key: 'whatsapp_templates', label: 'WhatsApp Apply Templates' },
    { key: 'interview', label: 'Interview Prep Access' },
  ],
  'interview_pack': [
    { key: 'cv_fix_downloads', label: '10 ATS CV Improvements' },
    { key: 'interview', label: 'Interview Prep Access' },
    { key: 'candidate_lock', label: 'Same Candidate Support' },
  ],
  'pro': [
    { key: 'cv_fix_downloads', label: '2 ATS CV Fixes' },
    { key: 'cover_letter_generations', label: '4 Cover Letters' },
    { key: 'email_templates', label: '4 Email Templates' },
    { key: 'whatsapp_templates', label: '4 WhatsApp Templates' },
    { key: 'english_test', label: '1 English Assessment' },
  ],
  'free': [
    { key: 'cv_check', label: '1 Free CV Check' },
  ],
};

/**
 * Maps entitlement product_type to plan tier key
 */
function mapEntitlementToPlan(productType) {
  const pt = (productType || '').toLowerCase();
  if (pt.includes('career_boost_elite')) return 'career_boost_elite';
  if (pt.includes('templates') || pt.includes('job_apply_kit')) return 'job_apply_kit';
  if (pt.includes('interview_pack')) return 'interview_pack';
  if (pt.includes('pro')) return 'pro';
  if (pt.includes('beta_tester')) return 'career_boost_elite';
  if (pt.includes('cv_fix')) return 'pro';
  return null;
}

/**
 * Hook to determine user's active plan from entitlements
 */
export function useActivePlan(user) {
  const [state, setState] = useState({
    activePlan: user?.subscription_plan || 'free',
    planLabel: 'Free',
    planColor: 'text-gray-600',
    planBadge: 'bg-gray-100 text-gray-700',
    isPaid: false,
    entitlements: [],
    loading: true,
    expiresAt: null,
    usageRemaining: {},
    benefits: PLAN_BENEFITS['free'],
  });

  useEffect(() => {
    if (!user) {
      setState(s => ({ ...s, loading: false }));
      return;
    }
    fetchEntitlements();
  }, [user?.id]);

  const fetchEntitlements = async () => {
    try {
      const res = await axios.get(`${API}/billing/entitlements`);
      const entitlements = res.data.entitlements || [];
      const activeEnts = entitlements.filter(e => e.status === 'active');

      if (activeEnts.length === 0) {
        const tier = PLAN_TIERS['free'];
        setState({
          activePlan: 'free', planLabel: tier.label, planColor: tier.color,
          planBadge: tier.badge, isPaid: false, entitlements: activeEnts,
          loading: false, expiresAt: null, usageRemaining: {},
          benefits: PLAN_BENEFITS['free'],
        });
        return;
      }

      let highestRank = 0;
      let highestKey = 'free';
      let highestEnt = null;

      for (const ent of activeEnts) {
        const planKey = mapEntitlementToPlan(ent.product_type);
        if (planKey && PLAN_TIERS[planKey] && PLAN_TIERS[planKey].rank > highestRank) {
          highestRank = PLAN_TIERS[planKey].rank;
          highestKey = planKey;
          highestEnt = ent;
        }
      }

      // Use ONLY the highest-tier entitlement's usage_remaining (no merging)
      const usageFromHighest = highestEnt?.usage_remaining || {};

      const tier = PLAN_TIERS[highestKey] || PLAN_TIERS['free'];
      setState({
        activePlan: highestKey,
        planLabel: tier.label,
        planColor: tier.color,
        planBadge: tier.badge,
        isPaid: highestRank > 0,
        entitlements: activeEnts,
        loading: false,
        expiresAt: highestEnt?.expires_at || null,
        usageRemaining: usageFromHighest,
        benefits: PLAN_BENEFITS[highestKey] || PLAN_BENEFITS['free'],
      });
    } catch {
      const subPlan = user?.subscription_plan || 'free';
      const tier = PLAN_TIERS[subPlan] || PLAN_TIERS['free'];
      setState({
        activePlan: subPlan, planLabel: tier.label, planColor: tier.color,
        planBadge: tier.badge, isPaid: subPlan !== 'free', entitlements: [],
        loading: false, expiresAt: null, usageRemaining: {},
        benefits: PLAN_BENEFITS[subPlan] || PLAN_BENEFITS['free'],
      });
    }
  };

  return state;
}

export { PLAN_TIERS, BENEFIT_LABELS, PLAN_BENEFITS };
